/**
 * SearchSelect — a drop-in replacement for a <select> whose list you can type to filter
 * (Matt, 2026-10-15: "allow all dropdowns in the Import meeting notes dialogue to be searchable").
 *
 * Looks like the select it replaces (pass the same `style`). Click opens a list with a search box
 * already focused; typing filters by every word (all words must appear, any order); ↑/↓ move,
 * Enter picks, Escape closes. The list is portaled to <body> with position:fixed so a scrolling or
 * clipped modal never cuts it off, and it flips up when there is no room below.
 *
 * Props: value, options [{ value, label, disabled? }], onChange(value), disabled, style,
 *        placeholder (shown when nothing matches `value`), title.
 */
import { useState, useRef, useEffect, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'

const MAX_H = 280

export default function SearchSelect({ value, options, onChange, disabled, style, placeholder = 'Pick…', title }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [hi, setHi] = useState(0)
  const [pos, setPos] = useState(null)
  const btnRef = useRef(null)
  const listRef = useRef(null)
  const inputRef = useRef(null)

  const selected = (options || []).find(o => String(o.value) === String(value ?? ''))
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean)
  const shown = (options || []).filter(o => {
    const t = String(o.label || '').toLowerCase()
    return words.every(w => t.includes(w))
  })

  function place() {
    const b = btnRef.current && btnRef.current.getBoundingClientRect()
    if (!b) return
    const below = window.innerHeight - b.bottom
    const up = below < MAX_H + 50 && b.top > below
    setPos({ left: b.left, width: Math.max(b.width, 220), top: up ? null : b.bottom + 2, bottom: up ? window.innerHeight - b.top + 2 : null })
  }

  useLayoutEffect(() => { if (open) place() }, [open])
  useEffect(() => {
    if (!open) return
    const sel = Math.max(0, shown.findIndex(o => String(o.value) === String(value ?? '')))
    setHi(sel)
    const onDown = e => {
      if (listRef.current && listRef.current.contains(e.target)) return
      if (btnRef.current && btnRef.current.contains(e.target)) return
      close()
    }
    const onMove = e => { if (listRef.current && listRef.current.contains(e.target)) return; place() }
    document.addEventListener('mousedown', onDown, true)
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => { document.removeEventListener('mousedown', onDown, true); window.removeEventListener('scroll', onMove, true); window.removeEventListener('resize', onMove) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  useEffect(() => { setHi(0) }, [q])
  useEffect(() => {
    const el = listRef.current && listRef.current.querySelector('[data-hi="1"]')
    if (el) el.scrollIntoView({ block: 'nearest' })
  }, [hi, open])

  function close() { setOpen(false); setQ('') }
  function pick(o) { if (!o || o.disabled) return; close(); onChange && onChange(o.value) }
  function onKey(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(shown.length - 1, h + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(0, h - 1)) }
    else if (e.key === 'Enter') { e.preventDefault(); pick(shown[hi]) }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); btnRef.current && btnRef.current.focus() }
    else if (e.key === 'Tab') close()
  }

  return (
    <>
      <button type="button" ref={btnRef} disabled={disabled} title={title}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={e => { if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpen(true) } }}
        style={{ ...style, display: 'flex', alignItems: 'center', gap: 6, textAlign: 'left', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.7 : 1, minWidth: 0 }}>
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected ? selected.label : placeholder}</span>
        <span aria-hidden style={{ fontSize: '0.75em', opacity: 0.6 }}>▾</span>
      </button>
      {open && pos && createPortal(
        <div ref={listRef} style={{
          position: 'fixed', left: pos.left, width: pos.width, top: pos.top ?? undefined, bottom: pos.bottom ?? undefined,
          zIndex: 2147483000, background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 6,
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', maxHeight: MAX_H, overflow: 'hidden',
        }}>
          <input ref={inputRef} autoFocus value={q} onChange={e => setQ(e.target.value)} onKeyDown={onKey} placeholder="Type to search…"
            style={{ margin: 6, padding: '6px 8px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, outline: 'none' }} />
          <div style={{ overflowY: 'auto', paddingBottom: 4 }}>
            {shown.length === 0 && <div style={{ padding: '6px 10px', fontSize: 12, color: 'var(--aj-text-muted)', fontFamily: '"DM Sans",sans-serif' }}>No matches</div>}
            {shown.map((o, i) => {
              const isSel = String(o.value) === String(value ?? '')
              return (
                <div key={String(o.value) + ':' + i} data-hi={i === hi ? '1' : '0'}
                  onMouseEnter={() => setHi(i)} onMouseDown={e => { e.preventDefault(); pick(o) }}
                  style={{
                    padding: '6px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, lineHeight: 1.35,
                    cursor: o.disabled ? 'default' : 'pointer', color: o.disabled ? 'var(--aj-text-muted)' : 'var(--aj-text)',
                    background: i === hi && !o.disabled ? 'var(--aj-bg-muted)' : 'transparent', fontWeight: isSel ? 600 : 400,
                  }}>
                  {o.label}
                </div>
              )
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
